import CachedIcon from '@mui/icons-material/Cached'
import EditIcon from '@mui/icons-material/Edit'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import { FilterAltOff, Search } from '@mui/icons-material'
import PeopleIcon from '@mui/icons-material/People'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import PersonRemoveIcon from '@mui/icons-material/PersonRemove'
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    Collapse,
    Dialog,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    Tooltip
} from '@mui/material'
import { GridActionsCellItem } from '@mui/x-data-grid'
import { useEffect, useState } from 'react'
import { Controller } from 'react-hook-form'
import toast from 'react-hot-toast'
import { ControlledAutocomplete } from 'src/components/forms/ControlledAutocomplete'
import { CountryAvatar } from 'src/components/icons/CountryAvatar'
import { useConfirmationDialog } from 'src/hooks/useConfirmationDialog'
import { usePaisLogo } from 'src/hooks/usePaisLogo'
import { HttpGroup } from 'src/http/group/httpGroup'
import { HttpSystemsNewByGroup } from 'src/http/group/httpSystemsNewByGroup'
import {
    ItecColFilters,
    ItecDataGrid,
    ItecGridColsDef as GridColsDef,
    useItecDataGrid
} from 'src/lib/itaptec/datagrid/src'
import { ICON_MAS } from 'src/pages/sistema/components/SharedIcons'
import { EntityWithIdAndDescriptionFields, schemaSelectionOption } from 'src/types'
import { GroupDTOFields, GroupFilterDTOFields, GroupViewDTO, GroupViewDTOFields } from 'src/types/group/groupData'
import { z } from 'zod'
import { WrapperTable } from '../components'
import EditGroupUsers from './components/EditGroupUsers'
import GroupFormDialog from './components/GroupFormDialog'

const schemaFiltroGrupos = z.object({
    [GroupFilterDTOFields.Name]: z.string().optional(),
    [GroupFilterDTOFields.CodsPais]: z.array(schemaSelectionOption).optional(),
    [GroupFilterDTOFields.CodsTipoMensaje]: z
        .object({
            id: z.number(),
            descripcion: z.string()
        })
        .optional()
})

type SchemaFiltroGruposTipo = z.infer<typeof schemaFiltroGrupos>

type SistemaTipoOption = {
    id: number
    descripcion: string
}

const getSelectedIds = (value?: any) => {
    const values = Array.isArray(value) ? value : value ? [value] : []

    return values.map(item => item?.id ?? item?.value ?? item).filter(item => item !== undefined && item !== null)
}

const hasRequiredGroupFilter = (filter?: SchemaFiltroGruposTipo) =>
    getSelectedIds(filter?.[GroupFilterDTOFields.CodsPais]).length > 0 ||
    getSelectedIds(filter?.[GroupFilterDTOFields.CodsTipoMensaje]).length > 0

function TableGroups() {
    const { askConfirmation } = useConfirmationDialog()
    const { paisLogos } = usePaisLogo()
    const [filtersOpen, setFiltersOpen] = useState(true)
    const [showFormDialog, setShowFormDialog] = useState<boolean>(false)
    const [selectedGroup, setSelectedGroup] = useState<GroupViewDTO>()
    const [selectedGroupUsers, setSelectedGroupUsers] = useState<GroupViewDTO>()
    const [sistemasTipo, setSistemasTipo] = useState<SistemaTipoOption[]>([])
    const [loadingSistemasTipo, setLoadingSistemasTipo] = useState(false)

    const getGroupDisplayName = (group: GroupViewDTO) => group[GroupDTOFields.Name] || 'este grupo'

    useEffect(() => {
        let mounted = true

        setLoadingSistemasTipo(true)
        HttpSystemsNewByGroup.getSystemTypes()
            .then(res => {
                if (!mounted) return
                setSistemasTipo([
                    { id: -1, descripcion: 'Sin Sistema' },
                    ...res.map(s => ({
                        id: s.id,
                        descripcion: s.descripcion
                    }))
                ])
            })
            .finally(() => {
                if (mounted) setLoadingSistemasTipo(false)
            })

        return () => {
            mounted = false
        }
    }, [])

    const columnas: GridColsDef<GroupViewDTO>[] = [
        {
            headerName: 'Grupo',
            field: GroupDTOFields.Name,
            renderCell: ({ row }) => (
                <Stack direction='row' alignItems='center' spacing={1.5} sx={{ minWidth: 0 }}>
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            display: 'grid',
                            placeItems: 'center',
                            fontWeight: 700,
                            fontSize: 13
                        }}
                    >
                        {(row[GroupDTOFields.Name]?.[0] ?? '?').toUpperCase()}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.25 }}>
                            {row[GroupDTOFields.Name] || 'Sin nombre'}
                        </Box>
                        <Box
                            sx={{
                                fontSize: 12,
                                color: 'text.secondary',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                        >
                            {row[GroupViewDTOFields.ProfileName] || 'Sin perfil'}
                        </Box>
                    </Box>
                </Stack>
            ),
            flex: 3
        },
        {
            headerName: 'Perfil',
            field: GroupViewDTOFields.ProfileName,
            flex: 2
        },
        {
            headerName: 'Pais',
            field: 'pais',
            flex: 2,
            renderCell: ({ row: group }) => (
                <Chip
                    label={group[GroupViewDTOFields.CountryName]}
                    size='small'
                    variant='outlined'
                    avatar={<CountryAvatar countryCode={group[GroupViewDTOFields.CodPais]} />}
                />
            )
        },
        {
            headerName: 'Sistema',
            field: GroupViewDTOFields.NombreSistema,
            flex: 3
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Acciones',
            align: 'center',
            headerAlign: 'center',
            minWidth: 168,
            getActions: ({ row }) => getActions(row, false)
        }
    ]

    const filtros: ItecColFilters<SchemaFiltroGruposTipo> = {
        [GroupDTOFields.Name]: {
            type: 'text',
            name: GroupFilterDTOFields.Name,
            props: { debounceTime: 500, size: 'small' }
        },
        pais: {
            type: 'multiselect',
            name: GroupFilterDTOFields.CodsPais,
            props: { getOptions: () => Promise.resolve([]), size: 'small' }
        },
        [GroupViewDTOFields.NombreSistema]: {
            type: 'select',
            name: GroupFilterDTOFields.CodsTipoMensaje,
            props: { getOptions: () => Promise.resolve([]), size: 'small' }
        }
    }

    const getActions = (group: GroupViewDTO, small: boolean) => [
        group[GroupViewDTOFields.Active] ? (
            <GridActionsCellItem
                key={1}
                onClick={() =>
                    askConfirmation(() => {
                        const promise = HttpGroup.deactivateGroup(group.id)

                        toast
                            .promise(promise, {
                                success: 'Inactivado',
                                error: 'No se pudo inactivar',
                                loading: 'Inactivando'
                            })
                            .then(() => refetch())
                    }, `Esta seguro que desea inactivar el grupo ${getGroupDisplayName(group)}?`)
                }
                icon={
                    <Tooltip title='Inactivar'>
                        <PersonRemoveIcon color='error' />
                    </Tooltip>
                }
                label='Inactivar'
                showInMenu={small}
            />
        ) : (
            <GridActionsCellItem
                key={1}
                onClick={() =>
                    askConfirmation(() => {
                        const promise = HttpGroup.activateGroup(group.id)

                        toast
                            .promise(promise, {
                                success: 'Activado',
                                error: 'No se pudo activar',
                                loading: 'Activando'
                            })
                            .then(() => refetch())
                    })
                }
                icon={
                    <Tooltip title='Activar'>
                        <PersonAddIcon color='success' />
                    </Tooltip>
                }
                label='Activar'
                showInMenu={small}
            />
        ),
        <GridActionsCellItem
            key={2}
            icon={
                <Tooltip title='Editar'>
                    <EditIcon color='info' />
                </Tooltip>
            }
            label='Editar'
            showInMenu={small}
            onClick={() => {
                setSelectedGroup(group)
                setShowFormDialog(true)
            }}
        />,
        <GridActionsCellItem
            key={4}
            icon={
                <Tooltip title='Usuarios'>
                    <PeopleIcon color='primary' />
                </Tooltip>
            }
            label='Usuarios'
            showInMenu={small}
            onClick={() => {
                setSelectedGroupUsers(group)
            }}
        />
    ]

    const {
        datagridProps,
        control: {
            query: { refetch },
            form: filterForm
        }
    } = useItecDataGrid({
        pagination: true,
        columns: columnas,
        getEntitiesPaginated: (pagination, filter) => {
            const codsPais = getSelectedIds(filter?.[GroupFilterDTOFields.CodsPais])
            const codsTipoMensaje = getSelectedIds(filter?.[GroupFilterDTOFields.CodsTipoMensaje])

            if (!codsPais.length && !codsTipoMensaje.length) {
                return Promise.resolve({
                    lista: [],
                    paginacion: {
                        cantPages: 1,
                        actualPage: pagination.actualPage,
                        cantRecords: 0,
                        pageSize: pagination.pageSize
                    }
                })
            }

            return HttpGroup.getGroupList(pagination, {
                nombre: filter?.[GroupFilterDTOFields.Name],
                codsPais,
                codsTipoMensaje,
                [GroupFilterDTOFields.ActiveOnly]: true
            })
        },
        density: 'standard',
        filters: filtros
    })

    const hasListScope = hasRequiredGroupFilter(filterForm?.watch())

    const cleanDatagridProps = {
        ...datagridProps,
        columns: datagridProps.columns.map(col => ({
            ...col,
            disableColumnMenu: true,
            renderHeader: undefined
        })),
        slots: undefined
    }

    const handleClearFilters = () => {
        if (!filterForm) return
        filterForm.reset({
            [GroupFilterDTOFields.Name]: undefined,
            [GroupFilterDTOFields.CodsPais]: undefined,
            [GroupFilterDTOFields.CodsTipoMensaje]: undefined
        })
    }

    return (
        <WrapperTable>
            <Card
                elevation={0}
                sx={{
                    border: theme => `1px solid ${theme.palette.divider}`,
                    borderRadius: 1.5,
                    overflow: 'hidden'
                }}
            >
                <CardHeader
                    sx={{
                        px: 3,
                        py: 2.5,
                        '& .MuiCardHeader-title': { fontWeight: 700 },
                        '& .MuiCardHeader-subheader': { mt: 0.5 }
                    }}
                    title='Administracion de grupos'
                    subheader='Gestiona grupos, perfiles, paises, sistemas y usuarios asociados'
                    action={
                        <Stack direction='row' alignItems='center' spacing={1}>
                            <Tooltip title={filtersOpen ? 'Ocultar filtros' : 'Mostrar filtros'}>
                                <IconButton onClick={() => setFiltersOpen(prev => !prev)} size='small'>
                                    <FilterAltIcon color={filtersOpen ? 'primary' : 'inherit'} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title='Actualizar'>
                                <span>
                                    <IconButton disabled={datagridProps.loading} onClick={() => refetch()} size='small'>
                                        <CachedIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Button
                                variant='contained'
                                color='primary'
                                startIcon={ICON_MAS}
                                onClick={() => {
                                    setSelectedGroup(undefined)
                                    setShowFormDialog(true)
                                }}
                            >
                                Nuevo
                            </Button>
                        </Stack>
                    }
                />

                <Collapse in={filtersOpen}>
                    {filterForm && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 2,
                                alignItems: 'center',
                                mx: 3,
                                mb: 2,
                                p: 2,
                                borderRadius: 1,
                                border: theme => `1px solid ${theme.palette.divider}`,
                                bgcolor: 'background.default'
                            }}
                        >
                            <Box sx={{ minWidth: 210, flex: 1 }}>
                                <ControlledAutocomplete
                                    control={filterForm.control}
                                    name={GroupFilterDTOFields.CodsPais}
                                    label='Pais'
                                    loadOptions={() =>
                                        Promise.resolve(paisLogos).then(res =>
                                            res.map(s => ({
                                                id: s.id,
                                                descripcion: s[EntityWithIdAndDescriptionFields.Description]
                                            }))
                                        )
                                    }
                                    multiple
                                />
                            </Box>
                            <Box sx={{ minWidth: 210, flex: 1 }}>
                                <Controller
                                    control={filterForm.control}
                                    name={GroupFilterDTOFields.CodsTipoMensaje}
                                    render={({ field }) => {
                                        const selectedSistema = (field.value ?? null) as SistemaTipoOption | null

                                        return (
                                            <Autocomplete<SistemaTipoOption>
                                                options={sistemasTipo}
                                                loading={loadingSistemasTipo}
                                                value={selectedSistema}
                                                onChange={(_, value) => field.onChange(value ?? undefined)}
                                                getOptionLabel={option => option?.descripcion ?? ''}
                                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                                renderInput={params => (
                                                    <TextField {...params} label='Sistema' size='small' />
                                                )}
                                            />
                                        )
                                    }}
                                />
                            </Box>
                            <Controller
                                control={filterForm.control}
                                name={GroupFilterDTOFields.Name}
                                render={({ field }) => (
                                    <TextField
                                        {...field}
                                        value={field.value ?? ''}
                                        label='Grupo'
                                        size='small'
                                        sx={{ minWidth: 210, flex: 1 }}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position='start'>
                                                    <Search fontSize='small' />
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                )}
                            />
                            <Tooltip title='Limpiar filtros'>
                                <IconButton size='small' onClick={handleClearFilters}>
                                    <FilterAltOff fontSize='small' />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Collapse>

                <CardContent sx={{ pt: 0, px: 3, pb: 3 }}>
                    {!hasListScope && (
                        <Alert severity='info' sx={{ mb: 2 }}>
                            Seleccione al menos un pais o sistema para ver los grupos.
                        </Alert>
                    )}
                    <Box
                        sx={{
                            '& .MuiDataGrid-root': {
                                border: theme => `1px solid ${theme.palette.divider}`,
                                borderRadius: 1,
                                overflow: 'hidden'
                            },
                            '& .MuiDataGrid-columnHeaders': {
                                bgcolor: 'background.default'
                            },
                            '& .MuiDataGrid-row:hover': {
                                bgcolor: 'action.hover'
                            }
                        }}
                    >
                        <ItecDataGrid {...cleanDatagridProps} />
                    </Box>
                </CardContent>
            </Card>
            <>
                <GroupFormDialog
                    open={showFormDialog}
                    onClose={() => setShowFormDialog(false)}
                    group={selectedGroup}
                    maxWidth={'md'}
                    onAfterSubmit={() => {
                        refetch()
                        setSelectedGroup(undefined)
                        setShowFormDialog(false)
                    }}
                    onSubmitError={(e: string) => console.log(e)}
                />
                <Dialog open={!!selectedGroupUsers} fullWidth maxWidth='xl' keepMounted={false}>
                    {selectedGroupUsers && (
                        <EditGroupUsers
                            onClose={() => {
                                setSelectedGroupUsers(undefined)
                                refetch()
                            }}
                            group={selectedGroupUsers}
                        />
                    )}
                </Dialog>
            </>
        </WrapperTable>
    )
}

export default TableGroups
